import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { DateRange } from '@mui/x-date-pickers-pro'
import { useDispatch, useSelector } from 'react-redux'

dayjs.extend(utc)
import {
  Contractor,
  ContractorDetails,
  ExpirationIndicator,
  ReActivationFilterValue,
  RecollectionFilterValue,
} from 'src/interfaces/contractor'
import { mapRecollectionStepsToRecollections } from 'src/utils/collectedData'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  FetchContractor,
  FetchContractors,
  ExportContractorsCSV,
  FetchContractorsCounts,
  FetchSubcontractor,
} from 'src/constants/actionTypes'
import {
  contractorPageSizeOptions,
  contractorInitialSortFields,
  contractorColumns,
} from 'src/constants/contractor/contractorGrid'
import {
  contractorsTableHeaderTitles,
  contractorStatusTitles,
} from 'src/constants/contractor/constants'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'
import { IContractorsTableContext, ContractorsTableContext } from './ContractorsTable.context'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { getSelectedMarket } from 'src/selectors/market'
import { getOnboardingTemplates } from 'src/selectors/onboarding'
import { ContractorsHeader } from '../grid/ContractorsHeader'
import {
  ContractorsTableFields,
  ContractorState,
  ContractorStatus,
  SubcontractorHeaderTypes,
} from 'src/constants/contractor/enums'
import { ContractorDetailsModal } from 'src/components/contractor-details/ContractorDetailsModal'
import { handleApiError } from 'src/utils/errorHandlers'
import { Pagination } from 'src/interfaces/table'
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { ContractorsContext } from '../contractors/Contractors.context'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { EmptyContractors } from '../grid/EmptyContractors'
import { useGridColumnPreferences } from 'src/utils/hooks/grid/useGridColumnPreferences'
import { ExportDataModal } from 'src/components/shared/grid/export-data-modal/ExportDataModal'
import { downloadFileFromBuffer } from 'src/utils/fileDownload'
import { VehicleType, vehicleNamesMap } from 'src/constants/vehicle'
import { NO_VEHICLE_FILTER_VALUE } from '../shared/ContractorVehicleTypeSelect'

const RECOLLECTION_FILTER_TITLES: Record<RecollectionFilterValue, string> = {
  [RecollectionFilterValue.Manual]: 'Manual',
  [RecollectionFilterValue.Automatic]: 'Auto',
  [RecollectionFilterValue.Without]: 'Without recollection',
}

const EXPORT_FILTER_DATE_FORMAT = 'MMM D, YYYY'

const EXPIRATION_INDICATOR_TITLES: Record<ExpirationIndicator, string> = {
  [ExpirationIndicator.Valid]: 'More than 30 days',
  [ExpirationIndicator.ExpiringSoon]: '30 days and less',
  [ExpirationIndicator.Expired]: 'Expired',
}

interface FetchContractorsParams {
  limit?: number
  offset?: number
  searchQuery: string
  tagsFilter: Array<string>
  templateFilter: Array<string>
  statusFilter: Array<ContractorStatus>
  recollectionFilter: Array<RecollectionFilterValue>
  expirationIndicatorFilter: Array<ExpirationIndicator>
  vehicleTypeFilter: Array<string>
  reActivationFilter: Array<ReActivationFilterValue>
  onboardedAfter?: string
  onboardedBefore?: string
  sortModel: GridSortModel
  selectedState: ContractorState
  selectedSubcontractorId: string
}

interface ContractorsTableProps {
  selectedState: ContractorState
  selectedSubcontractorId: string
}

const getRowId = (row: Contractor) => row.id

const LOCKED_COLUMNS = ['externalId']

const ContractorsTableComponent = ({
  selectedState,
  selectedSubcontractorId,
}: ContractorsTableProps) => {
  const dispatch = useDispatch()

  const selectedMarket = useSelector(getSelectedMarket)

  const { searchQuery } = useContext(ContractorsContext)

  const { paginationModel, limit, offset, setPaginationModel } =
    useGridPagination(contractorPageSizeOptions)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [contractors, setContractors] = useState<Array<Contractor>>([])
  const [totalContractors, setTotalContractors] = useState<number>(0)
  const [statusFilter, setStatusFilter] = useState<Array<ContractorStatus>>([])
  const [tagsFilter, setTagsFilter] = useState<Array<string>>([])
  const [templateFilter, setTemplateFilter] = useState<Array<string>>([])
  const [recollectionFilter, setRecollectionFilter] = useState<Array<RecollectionFilterValue>>([])
  const [expirationIndicatorFilter, setExpirationIndicatorFilter] = useState<
    Array<ExpirationIndicator>
  >([])
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<Array<string>>([])
  const [reActivationFilter, setReActivationFilter] = useState<Array<ReActivationFilterValue>>([])
  const [dateOnboardedRange, setDateOnboardedRange] = useState<DateRange<Dayjs>>([null, null])
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null)
  const [contractorDetails, setContractorDetails] = useState<ContractorDetails | null>(null)
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false)
  const [sortModel, setSortModel] = useState<GridSortModel>(
    contractorInitialSortFields[selectedState]
      ? [
          {
            field: contractorInitialSortFields[selectedState] || 'invitedAt',
            sort: 'desc',
          },
        ]
      : [],
  )

  // Only the Active table has a "Date onboarded" column, so the filter is inert elsewhere.
  const [onboardedAfter, onboardedBefore] = useMemo(() => {
    if (selectedState !== ContractorState.Active) {
      return [undefined, undefined]
    }

    const [start, end] = dateOnboardedRange

    return [
      start ? dayjs.utc(start.format('YYYY-MM-DD')).startOf('day').toISOString() : undefined,
      end ? dayjs.utc(end.format('YYYY-MM-DD')).endOf('day').toISOString() : undefined,
    ]
  }, [dateOnboardedRange, selectedState])

  const fetchContractors = useCallback(
    async ({
      limit,
      offset,
      searchQuery,
      sortModel,
      selectedState,
      tagsFilter,
      templateFilter,
      statusFilter,
      recollectionFilter,
      expirationIndicatorFilter,
      vehicleTypeFilter,
      reActivationFilter,
      onboardedAfter,
      onboardedBefore,
      selectedSubcontractorId,
    }: FetchContractorsParams) => {
      setIsLoading(true)

      try {
        const params: any = {
          limit,
          offset,
          contractorState: selectedState,
          contractorStatus: statusFilter,
        }

        if (selectedSubcontractorId === SubcontractorHeaderTypes.SUBCONTRACTOR) {
          params.onlySubcontractors = true
        }

        if (selectedSubcontractorId === SubcontractorHeaderTypes.OWN) {
          params.masterContractorRelationId = 'null'
        }

        if (
          selectedSubcontractorId !== SubcontractorHeaderTypes.ALL &&
          selectedSubcontractorId !== SubcontractorHeaderTypes.OWN &&
          selectedSubcontractorId !== SubcontractorHeaderTypes.SUBCONTRACTOR
        ) {
          params.masterContractorRelationId = selectedSubcontractorId
        }

        if (searchQuery) {
          params.contractorSearch = searchQuery
        }

        if (selectedMarket?.id) {
          params.partnerMarketId = selectedMarket?.id
        }

        if (tagsFilter?.length) {
          params.tags = tagsFilter
        }

        if (templateFilter?.length) {
          params.collectDataTemplateIds = templateFilter
        }

        if (recollectionFilter?.length) {
          params.recollection = recollectionFilter
        }

        if (expirationIndicatorFilter?.length) {
          params.expirationIndicator = expirationIndicatorFilter
        }

        if (vehicleTypeFilter?.length) {
          params.vehicleType = vehicleTypeFilter
        }

        if (onboardedAfter) {
          params.onboardedAfter = onboardedAfter
        }

        if (onboardedBefore) {
          params.onboardedBefore = onboardedBefore
        }

        // Only send when a single option is selected — selecting both is equivalent to no filter.
        if (reActivationFilter?.length === 1) {
          params.autoActivationEnabled = reActivationFilter[0] === ReActivationFilterValue.Enabled
        }

        if (sortModel && sortModel[0]) {
          params.sort = `${sortModel[0]?.field}:${sortModel[0]?.sort?.toUpperCase()}`
        }

        const payload: { data: Array<Contractor>; pagination: Pagination } =
          await createAsyncAction(dispatch, FetchContractors.request(params))

        const contractorsWithRecollections = payload.data.map((contractor) => {
          return {
            ...contractor,
            recollections: mapRecollectionStepsToRecollections(contractor.recollectionSteps),
          }
        })

        setContractors(contractorsWithRecollections)
        setTotalContractors(payload.pagination.total)
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    },
    [selectedMarket?.id],
  )

  const fetchContractorsCounts = useCallback(() => {
    const params: any = {}

    if (selectedMarket?.id) {
      params.partnerMarketId = selectedMarket?.id
    }

    if (selectedSubcontractorId === SubcontractorHeaderTypes.SUBCONTRACTOR) {
      params.onlySubcontractors = true
    }

    if (selectedSubcontractorId === SubcontractorHeaderTypes.OWN) {
      params.masterContractorRelationId = 'null'
    }

    if (
      selectedSubcontractorId !== SubcontractorHeaderTypes.ALL &&
      selectedSubcontractorId !== SubcontractorHeaderTypes.OWN &&
      selectedSubcontractorId !== SubcontractorHeaderTypes.SUBCONTRACTOR
    ) {
      params.masterContractorRelationId = selectedSubcontractorId
    }

    dispatch(FetchContractorsCounts.request(params))
  }, [selectedMarket?.id, selectedSubcontractorId])

  const refreshContractors = useCallback(async () => {
    await fetchContractors({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      templateFilter,
      statusFilter,
      recollectionFilter,
      expirationIndicatorFilter,
      vehicleTypeFilter,
      reActivationFilter,
      onboardedAfter,
      onboardedBefore,
      selectedState,
      selectedSubcontractorId,
    })
    fetchContractorsCounts()
  }, [
    limit,
    offset,
    searchQuery,
    sortModel,
    tagsFilter,
    templateFilter,
    statusFilter,
    recollectionFilter,
    expirationIndicatorFilter,
    vehicleTypeFilter,
    reActivationFilter,
    onboardedAfter,
    onboardedBefore,
    selectedState,
    fetchContractors,
    fetchContractorsCounts,
    selectedSubcontractorId,
  ])

  useEffect(() => {
    fetchContractors({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      templateFilter,
      statusFilter,
      recollectionFilter,
      expirationIndicatorFilter,
      vehicleTypeFilter,
      reActivationFilter,
      onboardedAfter,
      onboardedBefore,
      selectedState,
      selectedSubcontractorId,
    })
    fetchContractorsCounts()
  }, [
    limit,
    offset,
    searchQuery,
    sortModel,
    tagsFilter,
    templateFilter,
    statusFilter,
    recollectionFilter,
    expirationIndicatorFilter,
    vehicleTypeFilter,
    reActivationFilter,
    onboardedAfter,
    onboardedBefore,
    selectedState,
    selectedMarket?.id,
    selectedSubcontractorId,
  ])

  const contractorDetailsRequestRef = useRef(0)

  const handleRowClick = useCallback(({ row }: { row: Contractor }) => {
    setSelectedContractor(row)
  }, [])

  const handleCloseDetails = useCallback(() => {
    contractorDetailsRequestRef.current += 1
    setSelectedContractor(null)
    setContractorDetails(null)
  }, [])

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel)
  }, [])

  const fetchContractorDetails = useCallback(
    async (contractor: Contractor) => {
      const requestId = ++contractorDetailsRequestRef.current

      try {
        const contractorDetails: ContractorDetails = contractor.masterContractorRelationId
          ? await createAsyncAction(
              dispatch,
              FetchSubcontractor.request({
                partnerDriverId: contractor.id,
                contractorState: selectedState,
              }),
            )
          : await createAsyncAction(
              dispatch,
              FetchContractor.request({
                contractorId: contractor.id,
                contractorState: selectedState,
              }),
            )

        if (contractorDetailsRequestRef.current === requestId) {
          setContractorDetails(contractorDetails)
        }
      } catch (err) {
        if (contractorDetailsRequestRef.current === requestId) {
          handleApiError(err)
        }
      }
    },
    [selectedState],
  )

  useEffect(() => {
    if (!selectedContractor) {
      contractorDetailsRequestRef.current += 1
      setContractorDetails(null)
    } else {
      fetchContractorDetails(selectedContractor)
    }
  }, [selectedContractor?.id])

  const refreshContractorDetails = useCallback(async () => {
    if (selectedContractor) {
      await fetchContractorDetails(selectedContractor)
    }

    refreshContractors()
  }, [selectedContractor?.id, refreshContractors])

  const { hasPermission } = useCheckPermissions()

  const isSubcontractor = useMemo(() => {
    return Boolean(selectedContractor?.masterContractorRelationId)
  }, [selectedContractor?.masterContractorRelationId])

  const columns = useMemo(() => {
    const cols = contractorColumns[selectedState]

    if (hasPermission(UserPermission.TagsView)) {
      return cols
    }

    return cols.filter((col) => col.field !== 'tags')
  }, [selectedState, hasPermission])

  const {
    columnVisibilityModel,
    pinnedColumns,
    orderedColumns,
    onColumnVisibilityModelChange,
    onPinnedColumnsChange,
    onColumnOrderChange,
    onToggleColumnVisibility,
    onPinColumn,
    onUnpinColumn,
  } = useGridColumnPreferences({
    tableKey: `contractors-${selectedState}`,
    columns,
    lockedColumns: LOCKED_COLUMNS,
  })

  const onboardingTemplates = useSelector(getOnboardingTemplates)

  const exportColumns = useMemo(() => {
    return orderedColumns
      .filter((column) => {
        return column.field !== 'actions' && columnVisibilityModel[column.field] !== false
      })
      .map((column) => {
        return { field: column.field, headerName: column.headerName || column.field }
      })
  }, [orderedColumns, columnVisibilityModel])

  const exportFilters = useMemo(() => {
    const filters: Array<{ label: string; value: string }> = []

    if (searchQuery) {
      filters.push({ label: 'Search', value: searchQuery })
    }

    if (statusFilter.length) {
      const statusTitles = statusFilter.map((status) => {
        return contractorStatusTitles[status]
      })

      filters.push({ label: 'Status', value: statusTitles.join(', ') })
    }

    if (tagsFilter.length) {
      filters.push({ label: 'Tags', value: tagsFilter.join(', ') })
    }

    if (templateFilter.length) {
      const templateNames = templateFilter.map((templateId) => {
        const template = onboardingTemplates.find((item) => {
          return item.id === templateId
        })

        return template?.name || templateId
      })

      filters.push({ label: 'Assigned Template', value: templateNames.join(', ') })
    }

    if (recollectionFilter.length) {
      const recollectionTitles = recollectionFilter.map((recollection) => {
        return RECOLLECTION_FILTER_TITLES[recollection]
      })

      filters.push({ label: 'Recollection', value: recollectionTitles.join(', ') })
    }

    if (expirationIndicatorFilter.length) {
      const expirationTitles = expirationIndicatorFilter.map((indicator) => {
        return EXPIRATION_INDICATOR_TITLES[indicator]
      })

      filters.push({ label: 'Expiration', value: expirationTitles.join(', ') })
    }

    if (vehicleTypeFilter.length) {
      const vehicleTypeTitles = vehicleTypeFilter.map((vehicleType) => {
        if (vehicleType === NO_VEHICLE_FILTER_VALUE) {
          return 'No vehicle'
        }

        return vehicleNamesMap[vehicleType as VehicleType] || vehicleType
      })

      filters.push({ label: 'Vehicle Type', value: vehicleTypeTitles.join(', ') })
    }

    if (onboardedAfter) {
      filters.push({
        label: 'Date onboarded from',
        value: dayjs.utc(onboardedAfter).format(EXPORT_FILTER_DATE_FORMAT),
      })
    }

    if (onboardedBefore) {
      filters.push({
        label: 'Date onboarded to',
        value: dayjs.utc(onboardedBefore).format(EXPORT_FILTER_DATE_FORMAT),
      })
    }

    return filters
  }, [
    searchQuery,
    statusFilter,
    tagsFilter,
    templateFilter,
    onboardingTemplates,
    recollectionFilter,
    expirationIndicatorFilter,
    vehicleTypeFilter,
    onboardedAfter,
    onboardedBefore,
  ])

  const handleOpenExportModal = useCallback(() => {
    setIsExportModalOpen(true)
  }, [])

  const handleCloseExportModal = useCallback(() => {
    setIsExportModalOpen(false)
  }, [])

  const handleExportContractors = useCallback(
    async (count: number) => {
      const exportColumnFields = exportColumns.map((column) => {
        return column.field
      })

      const params: any = {
        limit: count,
        contractorState: selectedState,
        contractorStatus: statusFilter,
        exportColumns: exportColumnFields.map((_field) => {
          const field = _field as ContractorsTableFields
          let key: string = field

          if (field === ContractorsTableFields.recollections) {
            key = 'recollectionSteps'
          }

          if (field === ContractorsTableFields.deactivationDetails) {
            key = 'deactivationReason'
          }

          return {
            header: contractorsTableHeaderTitles[field] || field,
            key,
          }
        }),
      }

      if (selectedSubcontractorId === SubcontractorHeaderTypes.SUBCONTRACTOR) {
        params.onlySubcontractors = true
      }

      if (selectedSubcontractorId === SubcontractorHeaderTypes.OWN) {
        params.masterContractorRelationId = 'null'
      }

      if (
        selectedSubcontractorId !== SubcontractorHeaderTypes.ALL &&
        selectedSubcontractorId !== SubcontractorHeaderTypes.OWN &&
        selectedSubcontractorId !== SubcontractorHeaderTypes.SUBCONTRACTOR
      ) {
        params.masterContractorRelationId = selectedSubcontractorId
      }

      if (searchQuery) {
        params.contractorSearch = searchQuery
      }

      if (selectedMarket?.id) {
        params.partnerMarketId = selectedMarket?.id
      }

      if (tagsFilter.length) {
        params.tags = tagsFilter
      }

      if (templateFilter.length) {
        params.collectDataTemplateIds = templateFilter
      }

      if (recollectionFilter.length) {
        params.recollection = recollectionFilter
      }

      if (expirationIndicatorFilter.length) {
        params.expirationIndicator = expirationIndicatorFilter
      }

      if (sortModel && sortModel[0]) {
        params.sort = `${sortModel[0]?.field}:${sortModel[0]?.sort?.toUpperCase()}`
      }

      if (onboardedAfter) {
        params.onboardedAfter = onboardedAfter
      }

      if (onboardedBefore) {
        params.onboardedBefore = onboardedBefore
      }

      if (vehicleTypeFilter.length) {
        params.vehicleType = vehicleTypeFilter
      }

      const buffer: ArrayBuffer = await createAsyncAction(
        dispatch,
        ExportContractorsCSV.request(params),
      )

      if (!buffer || buffer.byteLength === 0) {
        throw new Error('Empty export buffer')
      }

      downloadFileFromBuffer(buffer, 'contractors.csv', 'text/csv')
    },
    [
      dispatch,
      selectedState,
      statusFilter,
      exportColumns,
      selectedSubcontractorId,
      searchQuery,
      selectedMarket?.id,
      tagsFilter,
      templateFilter,
      recollectionFilter,
      expirationIndicatorFilter,
      sortModel,
      onboardedAfter,
      onboardedBefore,
      vehicleTypeFilter,
    ],
  )

  const contractorsContextValue = useMemo<IContractorsTableContext>(() => {
    return {
      statusFilter,
      setStatusFilter,

      tagsFilter,
      setTagsFilter,

      templateFilter,
      setTemplateFilter,

      recollectionFilter,
      setRecollectionFilter,

      expirationIndicatorFilter,
      setExpirationIndicatorFilter,

      vehicleTypeFilter,
      setVehicleTypeFilter,

      reActivationFilter,
      setReActivationFilter,

      dateOnboardedRange,
      setDateOnboardedRange,

      refreshContractors,

      columnsConfig: columns,
      columnVisibilityModel,
      pinnedColumns,
      lockedColumns: LOCKED_COLUMNS,
      onToggleColumnVisibility,
      onColumnVisibilityModelChange,
      onPinColumn,
      onUnpinColumn,
    }
  }, [
    statusFilter,
    setStatusFilter,
    tagsFilter,
    setTagsFilter,
    templateFilter,
    setTemplateFilter,
    recollectionFilter,
    setRecollectionFilter,
    expirationIndicatorFilter,
    setExpirationIndicatorFilter,
    vehicleTypeFilter,
    setVehicleTypeFilter,
    reActivationFilter,
    setReActivationFilter,
    dateOnboardedRange,
    setDateOnboardedRange,
    refreshContractors,
    columns,
    columnVisibilityModel,
    pinnedColumns,
    onToggleColumnVisibility,
    onColumnVisibilityModelChange,
    onPinColumn,
    onUnpinColumn,
  ])

  return (
    <ContractorsTableContext.Provider value={contractorsContextValue}>
      <ContractorsHeader selectedContractorState={selectedState} />
      <GridTable<Contractor>
        rows={contractors}
        isLoading={isLoading}
        columns={orderedColumns}
        paginationModel={paginationModel}
        pageSizeOptions={contractorPageSizeOptions}
        rowCount={totalContractors}
        sortModel={sortModel}
        columnVisibilityModel={columnVisibilityModel}
        pinnedColumns={pinnedColumns}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onPinnedColumnsChange={onPinnedColumnsChange}
        onColumnOrderChange={onColumnOrderChange}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handleChangePagination}
        onRowClick={handleRowClick}
        getRowId={getRowId}
        EmptyElement={EmptyContractors}
        tableDataTestId="contractors-list-main-table"
        rowDataTestIdPrefix="contractors-list-main"
        onExportClick={handleOpenExportModal}
      />
      {contractorDetails && (
        <ContractorDetailsModal
          key={contractorDetails.id}
          contractorDetails={contractorDetails}
          showWaitingForApprovalStepsFirst={selectedState === ContractorState.Recollection}
          flow="contractors"
          onClose={handleCloseDetails}
          onRefresh={refreshContractorDetails}
          isSubcontractor={isSubcontractor}
        />
      )}
      <ExportDataModal
        isOpen={isExportModalOpen}
        onClose={handleCloseExportModal}
        entityNamePlural="contractors"
        columns={exportColumns}
        filters={exportFilters}
        defaultCount={Math.min(totalContractors, limit)}
        maxCount={totalContractors}
        onExport={handleExportContractors}
      />
    </ContractorsTableContext.Provider>
  )
}

export const ContractorsTable = memo(ContractorsTableComponent)
